import { FC } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";

import { ExportDetails } from "../../../types/niffler";
import { StatusBadge } from "./components/StatusBadge";
import {
  formatDate,
  mapExportType,
  getIncludedDataItems,
  formatSearchCriteria,
} from "./utils/helpers";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import Stack from "@mui/material/Stack";

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow: FC<DetailRowProps> = ({ label, value }) => (
  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
    <Typography variant="body1" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body1" component="span">
      {value}
    </Typography>
  </Stack>
);

const SectionHeader: FC<{
  title: string;
  mb?: number;
  "data-testid"?: string;
}> = ({ title, mb = 2, "data-testid": dataTestId }) => (
  <Typography
    data-testid={dataTestId}
    variant="h4"
    component="h3"
    sx={{ mb, fontWeight: 600 }}
  >
    {title}
  </Typography>
);

interface ExportDetailsModalProps {
  exportDetails: ExportDetails | null;
  open: boolean;
  onClose: () => void;
}

export const ExportDetailsModal: FC<ExportDetailsModalProps> = ({
  exportDetails,
  open,
  onClose,
}) => {
  if (!exportDetails) {
    return null;
  }

  const searchCriteria = formatSearchCriteria(
    exportDetails.params?.humanFilterCriteria
  );

  const includedDataItems = getIncludedDataItems(exportDetails);
  const hasIncludedData = includedDataItems.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="export-details-dialog-title"
    >
      <DialogTitle id="export-details-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" component="h2">
            Export Details
          </Typography>
          <IconButton
            data-testid="export-details-close-btn"
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ ...lightScrollbarStyle }}>
        <Box>
          {/* Section 1: General Information */}
          <SectionHeader
            data-testid="export-details-general-section"
            title="General"
          />
          <DetailRow label="Export ID:" value={exportDetails.id} />
          <DetailRow
            label="Export Type:"
            value={mapExportType(exportDetails)}
          />
          <DetailRow
            label="Status:"
            value={<StatusBadge status={exportDetails.status} size="small" />}
          />
          <DetailRow
            label="Requested At:"
            value={formatDate(exportDetails.dateTime)}
          />
          <DetailRow
            label="Completed At:"
            value={
              exportDetails.completedDateTime
                ? formatDate(exportDetails.completedDateTime)
                : "-"
            }
          />

          {hasIncludedData && (
            <>
              <Divider sx={{ my: 2 }} />
              <SectionHeader
                data-testid="export-details-included-data-section"
                title="Included Data"
                mb={1}
              />
              <Box>
                {includedDataItems.map((item, index) => (
                  <Box key={index} sx={{ py: 0.5 }}>
                    <Typography variant="body1">
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        • {item.dataType}:
                      </Box>{" "}
                      {item.inclusion}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Section 3: Search Criteria */}
          <SectionHeader
            data-testid="export-details-criteria-section"
            title="Search Criteria"
            mb={1}
          />
          {searchCriteria.length > 0 ? (
            <List dense>
              {searchCriteria.map((criterion, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText
                    primary={`• ${criterion}`}
                    primaryTypographyProps={{
                      variant: "body1",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No specific criteria applied
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
